import { useRouter } from 'expo-router'
import { useEffect, type ReactNode } from 'react'
import { View } from 'react-native'

import { PlaceholderLine } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { PrayRewardScreen } from '@/src/features/pray/ui/screens/PrayRewardScreen'
import { useXMRPMRedirect } from '@/src/features/rosario/services/useXMRPMRedirect'
import { useAskWatchman } from '@/src/features/watchman/services/useAskWatchman'
import { useWatchmanInitialIntentionStore } from '@/src/features/watchman/services/useBecomeWatchman'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TTypeIntention } from '../../models/Intention.types'
import { useIntentionQuery } from '../../services/useIntentionQuery'
import { UpliftForm } from '../components/UpliftForm'

const SuccessDetails = ({
  error,
  intention,
}: {
  error?: Error | null
  intention?: Pick<TTypeIntention, 'prayersCount'>
}) => {
  const { t } = useTranslate()

  if (error)
    return (
      <ErrorMessage
        error={error}
        defaultErrorKey="intention.load.error"
        testID={TEST_ID.INTENTION.REWARD.SCREEN.ERROR}
      />
    )
  if (!intention) return <PlaceholderLine testID={TEST_ID.INTENTION.REWARD.SCREEN.LOADING} />
  if (intention.prayersCount && intention.prayersCount > 0)
    return (
      <Typography colors="primary" className="py-1">
        {t('intention.prayed.with', { count: intention.prayersCount })}
      </Typography>
    )
  return null
}

type TIntentionRewardScreen = TSourceParam & {
  idSharetoken: TIdDashSlug
  type: INTENTION_TYPE
  redirect: string
  prayerId: number
  theme: string | undefined
}

export const IntentionRewardScreen = ({
  idSharetoken,
  source,
  redirect,
  type: intentionType,
  prayerId,
  theme,
}: TIntentionRewardScreen): ReactNode => {
  const isLoggedIn = getIsLoggedIn()
  const { data: me } = useMeQuery()
  const [intentionId, shareToken] = parseIdDash(idSharetoken)
  const { intention, error } = useIntentionQuery(intentionId, shareToken, intentionType)
  const { shouldAsk: shouldAskBecomeWatchman, isPending: isSettingsPending } = useAskWatchman({
    askAnonymousUsers: true,
  })
  const saveIntention = useWatchmanInitialIntentionStore((state) => state.saveIntention)

  const router = useRouter()
  const { routes } = useRoutes()

  const askWatchmanRedirect = routes.askWatchman({ redirect, source, theme })
  const postRewardRedirect = useXMRPMRedirect({ destination: redirect })

  const handleContinue = () => {
    GTM.sendEvent({ event: EVENTS.POST_PRAY_INTENTION_POPIN_CONTINUE }, { trackingSource: source })
    saveIntention(intentionId, intentionType)
    router.dismissTo(shouldAskBecomeWatchman ? askWatchmanRedirect : postRewardRedirect)
  }

  useEffect(() => {
    GTM.sendEvent({ event: EVENTS.POST_PRAY_INTENTION_POPIN_DISPLAYED }, { trackingSource: source })
  }, [source])

  return (
    <PrayRewardScreen
      isLoading={isSettingsPending && isLoggedIn}
      onContinue={handleContinue}
      successDetails={<SuccessDetails error={error} intention={intention} />}
      testID={TEST_ID.INTENTION.REWARD.SCREEN.NAME}
    >
      {intentionType === INTENTION_TYPE.INTENTION && intention?.user?.name && me?.id && (
        <View className="max-w-[500px] flex-col gap-6">
          <UpliftForm
            userName={intention.user.name}
            intentionId={intentionId}
            prayerId={prayerId}
            onDisplay={() => GTM.sendEvent(EVENTS.POST_PRAY_INTENTION_UPLIFT_FORM_DISPLAYED)}
            onSubmit={() => GTM.sendEvent(EVENTS.POST_PRAY_INTENTION_UPLIFT_LEAVE_INTENTION)}
          />
        </View>
      )}
    </PrayRewardScreen>
  )
}
